import React, { useCallback, useEffect, useState } from 'react';
import {
  getPendingScheduledAction,
  approvePendingScheduledAction,
  declinePendingScheduledAction
} from '../../utils/api';
import EmailPreviewCard from './EmailPreviewCard';
import { ActionCard, Button } from './response/primitives';
import './ScheduledTaskApprovalCard.css';

/**
 * ScheduledTaskApprovalCard — the moment a recurring automation becomes real.
 *
 * The assistant emits a ```scheduled_task_approval fence carrying nothing but
 * an id. Everything shown here is fetched from the server, deliberately: the
 * spec the user reads is the spec the server will execute, not a description
 * the model wrote about its own pending write. Approving posts to the server,
 * which is the only path that commits.
 *
 * The consequence line sits directly above the button because that is the
 * sentence people should have read before they agree — who it mails, how
 * often, and whether it can change anything on its own.
 */

const ACTION_VERB = {
  create: 'Approve & schedule',
  update: 'Approve & save',
  delete: 'Approve & delete',
  run: 'Approve & send'
};

const DONE_LABEL = {
  create: 'Scheduled',
  update: 'Saved',
  delete: 'Deleted',
  run: 'Sent'
};

const ScheduledTaskApprovalCard = ({ data, onAnswer }) => {
  const pendingActionId = data?.pendingActionId;
  const [state, setState] = useState({ loading: true, error: null, spec: null });
  const [busy, setBusy] = useState(false);
  const [resolution, setResolution] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [createdTaskId, setCreatedTaskId] = useState(null);

  const load = useCallback(async () => {
    if (!pendingActionId) {
      setState({ loading: false, error: 'This request is no longer available.', spec: null });
      return;
    }
    setState({ loading: true, error: null, spec: null });
    try {
      const spec = await getPendingScheduledAction(pendingActionId);
      setState({ loading: false, error: null, spec });
      // A card restored from conversation history may already have been
      // acted on. Show that rather than offering the button again.
      if (spec.status && spec.status !== 'pending') setResolution(spec.status);
    } catch (err) {
      setState({
        loading: false,
        error: err?.response?.data?.error || err.message,
        spec: null
      });
    }
  }, [pendingActionId]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const result = await approvePendingScheduledAction(pendingActionId);
      setCreatedTaskId(result?.taskId || null);
      setResolution('approved');
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await declinePendingScheduledAction(pendingActionId);
      setResolution('declined');
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <div className="stac stac--muted">Loading request…</div>;
  }
  if (state.error) {
    return <div className="stac stac--muted">{state.error}</div>;
  }

  const { spec } = state;
  const action = spec.action || 'create';
  const destructive = action === 'delete';
  const details = Array.isArray(spec.details) ? spec.details : [];
  const detail = (label) => (details.find(([k]) => k === label) || [])[1];
  const when = detail('When');
  const sendsTo = detail('Sends to');
  const contains = detail('Contains');
  const longInstructions = typeof contains === 'string' && contains.length > 90;
  const chips = [
    detail('Property') ? `Property ${detail('Property')}` : null,
    detail('Delivered by') ? `${String(detail('Delivered by')).replace(/^email$/i, 'Email')} delivery` : null,
    detail('CC') ? `CC ${detail('CC')}` : null,
    !longInstructions && contains ? contains : null,
    detail('Sends when') ? detail('Sends when') : null,
    detail('Can change data') ? `Can change data: ${detail('Can change data')}` : null,
  ].filter(Boolean);
  const sub = [when, sendsTo && sendsTo !== '—' ? `→ ${sendsTo}` : null].filter(Boolean).join(' ');

  if (resolution === 'approved') {
    // Creating or changing an automation is the moment to see what it will
    // actually send. That preview used to follow the write automatically;
    // now that the write happens on this click, it belongs here.
    const showsPreview = (action === 'create' || action === 'update') && createdTaskId;
    return (
      <>
        <ActionCard
          icon="clock"
          title={spec.title}
          sub={sub || null}
          pill={{ state: 'done', label: DONE_LABEL[action] || 'Done' }}
          chips={chips}
          destructive={destructive}
          done={{ tone: 'success', text: `${DONE_LABEL[action] || 'Done'} — ${spec.title}${when ? `. Runs ${when}.` : '.'}` }}
        />
        {showsPreview && (
          <EmailPreviewCard
            data={{ taskId: createdTaskId, taskName: spec.title }}
            onAnswer={onAnswer}
          />
        )}
      </>
    );
  }
  if (resolution === 'declined') {
    return (
      <ActionCard icon="clock" title={spec.title} sub={sub || null} pill={{ state: 'muted', label: 'Dismissed' }}
        done={{ tone: 'muted', text: 'Dismissed — nothing was changed.' }} />
    );
  }
  if (resolution === 'expired') {
    return (
      <ActionCard icon="clock" title={spec.title} sub={sub || null} pill={{ state: 'muted', label: 'Expired' }}
        done={{ tone: 'muted', text: 'This request expired. Ask again to get a fresh one.' }} />
    );
  }

  return (
    <ActionCard
      icon="clock"
      title={spec.title}
      sub={sub || null}
      pill={{ state: 'pending', label: 'Needs your approval' }}
      chips={chips}
      disclosure={longInstructions ? { label: 'View full instructions', content: contains } : null}
      note={spec.consequence}
      error={actionError}
      destructive={destructive}
      footer={(
        <>
          <Button variant="primary" onClick={approve} disabled={busy}>
            {busy ? 'Working…' : (ACTION_VERB[action] || 'Approve')}
          </Button>
          <Button variant="ghost" onClick={decline} disabled={busy}>Not now</Button>
        </>
      )}
    />
  );
};

export default ScheduledTaskApprovalCard;
